import React from 'react';
import { notification } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  showSuccessNotification,
  showErrorNotification,
} from 'services/common';
import {
  configureEmailTypes,
  deleteEmailType,
  removeInvalidEmailCsv,
  sendMail,
  splitMicrosoftCsv,
  stopCSVMail,
  pauseCSVMail,
  resumeCSVMail,
  triggerCSVMail,
  watchCSVMail,
  unwatchCSVMail,
  saveMxRecordInCsv,
  simulateThrottleCSV,
  deleteBulkThrottleExemptions,
  addToThrottleExemptions,
  addToThrottleExemptionsCSV,
  watchEmailQueue,
  unwatchEmailQueue,
  addEmailQueueWatcher,
  removeEmailQueueWatcher,
} from 'services/mail';

const getBasenameFromFile = (filename: string) => {
  const arr = filename.split('.');
  arr.pop();
  return arr.join('.');
};

const handleDownloadBlob = (blob: Blob, filename: string) => {
  // create file link in browser's memory
  const href = URL.createObjectURL(blob);

  // create "a" HTML element with href to file & click
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', filename); //or any other extension
  document.body.appendChild(link);
  link.click();

  // clean up "a" element & remove ObjectURL
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

export const useSendMail = ({ onSuccess, history }) =>
  useMutation(sendMail, {
    onSuccess: async res => {
      onSuccess && onSuccess();
      const uploadedCSVId = res?.data?.id;
      if (uploadedCSVId) {
        showSuccessNotification(
          <div>
            <p>Request sent successfully</p>
            <a
              style={{ marginTop: 5 }}
              onClick={() => {
                notification.close(`csv-key:${uploadedCSVId}`);
                history.push(`/performance/csv#${uploadedCSVId}`);
              }}
            >
              Click to view CSV Performance
            </a>
          </div>,
          { duration: 20, key: `csv-key:${uploadedCSVId}` },
        );
      } else showSuccessNotification('Request sent successfully');
    },
    onError: () => {
      showErrorNotification('Request to send emails failed');
    },
  });

export const useSplitMicrosoftCsv = () => {
  return useMutation(
    file => {
      const formData = new FormData();
      formData.append('csv_file', file);
      return splitMicrosoftCsv(formData);
    },
    {
      onSuccess: (data, file) => {
        handleDownloadBlob(data, `${getBasenameFromFile(file.name)}.split.zip`);
        showSuccessNotification('CSV split successfully. Happy mailing');
      },
    },
  );
};
export const useRemoveInvalidEmailFromCsv = () => {
  return useMutation(
    file => {
      const formData = new FormData();
      formData.append('csv_file', file);
      return removeInvalidEmailCsv(formData);
    },
    {
      onSuccess: (data, file) => {
        handleDownloadBlob(data, `${getBasenameFromFile(file.name)}.clean.csv`);
        showSuccessNotification(
          'CSV clean off of invalid emails successfully. Happy mailing',
        );
      },
    },
  );
};

export const useSimulateThrottleCSV = () => {
  return useMutation(
    ({ file, payload }) => {
      const formData = new FormData();
      formData.append('csv_file', file);
      formData.append('payload', JSON.stringify(payload));
      return simulateThrottleCSV(formData);
    },
    {
      onSuccess: (data, file) => {
        handleDownloadBlob(
          data,
          `${getBasenameFromFile(file.file.name)}.throttle.csv`,
        );
        showSuccessNotification('Successfully simulated throttle csv');
      },
    },
  );
};

export const useSaveMxRecordInCsv = () => {
  return useMutation(
    file => {
      const formData = new FormData();
      formData.append('csv_file', file);
      return saveMxRecordInCsv(formData);
    },
    {
      onSuccess: () => {
        showSuccessNotification(
          'Successfully saved mx records of emails/email_domains in csv',
        );
      },
    },
  );
};

export const useAddToThrottleExemptions = () => {
  const queryClient = useQueryClient();
  return useMutation(
    file => {
      const formData = new FormData();
      formData.append('csv_file', file);
      return addToThrottleExemptionsCSV(formData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['throttle-exemption-list']);
        showSuccessNotification(
          `Successfully added email(s) to throttle exemptions`,
        );
      },
    },
  );
};

export const useQuickAddToThrottleExemptions = ({ onSuccess }) => {
  const queryClient = useQueryClient();
  return useMutation(addToThrottleExemptions, {
    onSuccess: () => {
      queryClient.invalidateQueries(['throttle-exemption-list']);
      showSuccessNotification(`Successfully email to throttle exemptions`);
      if (onSuccess) onSuccess();
    },
  });
};

export const useConfigureEmailTypes = () => {
  const queryClient = useQueryClient();
  return useMutation(
    data => {
      const cleanedData = data.map(item => {
        return {
          ...item,
          id: Number.isNaN(Number(item.id)) ? undefined : item.id,
        };
      });
      return configureEmailTypes(cleanedData);
    },
    {
      onSuccess: async () => {
        showSuccessNotification(`Update email types successfully`);
        queryClient.invalidateQueries(['emailTypes']).then(() => {});
      },
    },
  );
};

export const useDeleteEmailType = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteEmailType, {
    onSuccess: async () => {
      showSuccessNotification(`Delete email types successfully`);
      queryClient.invalidateQueries(['emailTypes']).then(() => {});
    },
  });
};

export const useDeleteBulkThrottleExemptions = ({ onSuccess }) => {
  const queryClient = useQueryClient();
  return useMutation((ids: string[]) => deleteBulkThrottleExemptions(ids), {
    onSuccess: async () => {
      queryClient.invalidateQueries(['throttle-exemption-list']);
      showSuccessNotification(
        `Successfully removed email(s) from throttle exemptions`,
      );
      if (onSuccess) onSuccess();
    },
  });
};

const optimisticCSVUpdate = ({ updated, queryClient, queryFilter }) => {
  queryClient.setQueryData(['uploadedCSV', queryFilter], data => {
    if (!data) return data;
    const { count, data: uploadedCSV } = data;
    return {
      count,
      data: uploadedCSV
        ? uploadedCSV.map(v => {
            if (Number(v.id) === Number(updated?.data?.id)) {
              return { ...v, ...updated.data };
            }
            return v;
          })
        : uploadedCSV,
    };
  });
  queryClient.setQueryData(
    ['get-one-csv-summary', { id: updated?.data?.id }],
    data => {
      if (!data) return data;
      return {
        ...data,
        ...updated?.data,
      };
    },
  );
  queryClient.invalidateQueries(['uploadedCSV', queryFilter]);
  queryClient.invalidateQueries([
    'get-one-csv-summary',
    { id: updated?.data?.id },
  ]);
};

export const useStopCSVMail = queryFilter => {
  const queryClient = useQueryClient();
  return useMutation(stopCSVMail, {
    onSuccess: async updated => {
      optimisticCSVUpdate({ updated, queryClient, queryFilter });
      showSuccessNotification(`CSV Aborted`);
    },
  });
};

export const useResumeCSVMail = queryFilter => {
  const queryClient = useQueryClient();
  return useMutation(resumeCSVMail, {
    onSuccess: async updated => {
      optimisticCSVUpdate({ updated, queryClient, queryFilter });
    },
  });
};

export const usePauseCSVMail = queryFilter => {
  const queryClient = useQueryClient();
  return useMutation(pauseCSVMail, {
    onSuccess: async updated => {
      optimisticCSVUpdate({ updated, queryClient, queryFilter });
    },
  });
};

export const useTriggerCSVMail = queryFilter => {
  const queryClient = useQueryClient();
  return useMutation(triggerCSVMail, {
    onSuccess: async updated => {
      optimisticCSVUpdate({ updated, queryClient, queryFilter });
    },
  });
};

export const useWatchCSVMail = id => {
  const queryClient = useQueryClient();
  return useMutation(watchCSVMail, {
    onSuccess: async updated => {
      queryClient.setQueryData(['get-csv-watch-status', { id }], () => updated);
    },
  });
};

export const useUnWatchCSVMail = id => {
  const queryClient = useQueryClient();
  return useMutation(unwatchCSVMail, {
    onSuccess: async updated => {
      queryClient.setQueryData(['get-csv-watch-status', { id }], () => updated);
    },
  });
};

export const useWatchEmailQueue = () => {
  const queryClient = useQueryClient();
  return useMutation(watchEmailQueue, {
    onSuccess: async () => {
      void queryClient.invalidateQueries(['email-queue-watcher']);
      showSuccessNotification('Watch email queue successfully');
    },
  });
};

export const useUnwatchEmailQueue = () => {
  const queryClient = useQueryClient();
  return useMutation(unwatchEmailQueue, {
    onSuccess: async () => {
      void queryClient.invalidateQueries(['email-queue-watcher']);
      showSuccessNotification('Unwatch email queue successfully');
    },
  });
};

export const useAddEmailQueueWatcher = () => {
  const queryClient = useQueryClient();
  return useMutation(addEmailQueueWatcher, {
    onSuccess: async () => {
      void queryClient.invalidateQueries(['email-queue-watcher']);
    },
  });
};

export const useRemoveEmailQueueWatcher = () => {
  const queryClient = useQueryClient();
  return useMutation(removeEmailQueueWatcher, {
    onSuccess: async () => {
      void queryClient.invalidateQueries(['email-queue-watcher']);
    },
  });
};
